import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Loader2, Upload, FileText, LifeBuoy, MessageSquareOff, MessageSquare,
} from "lucide-react";
import { platformApi } from "../../lib/api";
import HelpArticleEditorModal, { HELP_CATEGORIES } from "./HelpArticleEditorModal";

const labelize = (c) => (c || "general").replace(/\b\w/g, (m) => m.toUpperCase());

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
}

export default function PlatformHelpCenter() {
  const [articles, setArticles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | article
  const [busyId, setBusyId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const fileInputRef = useRef(null);

  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const loadArticles = async () => {
    try {
      setArticles(await platformApi.helpCenter.listArticles());
    } catch {
      toast.error("Failed to load knowledge base articles");
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      setSettings(await platformApi.helpCenter.getChatbotSettings());
    } catch {
      toast.error("Failed to load chatbot settings");
    }
  };

  useEffect(() => {
    loadArticles();
    loadSettings();
  }, []);

  const remove = async (a) => {
    if (!window.confirm(`Delete article "${a.title}"? This can't be undone.`)) return;
    setBusyId(a.id);
    try {
      await platformApi.helpCenter.removeArticle(a.id);
      toast.success("Article deleted");
      loadArticles();
    } catch {
      toast.error("Failed to delete article");
    } finally {
      setBusyId(null);
    }
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await platformApi.helpCenter.bulkUpload(file);
      toast.success(`Upload complete — ${res.created} created, ${res.updated} updated${res.skipped ? `, ${res.skipped} skipped` : ""}`);
      loadArticles();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Bulk upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const updated = await platformApi.helpCenter.updateChatbotSettings(settings);
      setSettings(updated);
      toast.success("Chatbot settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const visibleArticles = (articles || []).filter((a) => categoryFilter === "all" || a.category === categoryFilter);

  return (
    <div className="space-y-6" data-testid="platform-help-center-tab">
      <div className="flex items-center gap-3">
        <span className="h-10 w-10 rounded-lg bg-cyan-500/15 text-cyan-300 flex items-center justify-center">
          <LifeBuoy className="h-5 w-5" />
        </span>
        <div>
          <h2 className="text-[16px] font-semibold text-white">Help Center</h2>
          <p className="text-[12.5px] text-slate-400 mt-0.5">Manage the Help &amp; Support knowledge base and chatbot behavior.</p>
        </div>
      </div>

      {/* ---- Knowledge base editor ---- */}
      <div className="rounded-xl border border-white/10 bg-[#0F1623] p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <h3 className="text-[14px] font-semibold text-slate-100">Knowledge Base</h3>
            <span className="text-[11.5px] text-slate-500">{articles ? `${articles.length} article${articles.length === 1 ? "" : "s"}` : ""}</span>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              data-testid="help-center-category-filter"
              className="h-9 px-2.5 rounded-md bg-[#0F1623] border border-white/10 text-[12.5px] text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
            >
              <option value="all">All categories</option>
              {HELP_CATEGORIES.map((c) => (
                <option key={c} value={c}>{labelize(c)}</option>
              ))}
            </select>
            <input ref={fileInputRef} type="file" accept=".csv,.json" className="hidden" onChange={onFileSelected} data-testid="help-center-bulk-upload-input" />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="help-center-bulk-upload-btn"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[12.5px] font-medium text-slate-200 bg-white/5 hover:bg-white/10 disabled:opacity-50"
            >
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload Knowledge Base
            </button>
            <button
              onClick={() => setEditing({})}
              data-testid="help-center-new-article-btn"
              className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-cyan-600 hover:bg-cyan-500 text-white text-[12.5px] font-semibold"
            >
              <Plus className="h-3.5 w-3.5" /> New Article
            </button>
          </div>
        </div>
        <p className="text-[11.5px] text-slate-500 mt-2">
          Bulk upload accepts a .csv or .json file where each row/object has a <code className="text-slate-400">title</code>, <code className="text-slate-400">category</code>, and <code className="text-slate-400">body</code>. Matching titles are updated; new ones are created as published articles.
        </p>

        <div className="mt-4 rounded-lg border border-white/10 overflow-hidden">
          {loading ? (
            <div className="py-14 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-500" /></div>
          ) : visibleArticles.length === 0 ? (
            <div className="py-14 text-center" data-testid="help-center-empty-state">
              <FileText className="h-7 w-7 text-slate-600 mx-auto mb-2" />
              <p className="text-[13px] text-slate-400">No articles in this category yet.</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {visibleArticles.map((a) => (
                <div key={a.id} className="p-3.5 flex items-center gap-3" data-testid={`help-article-row-${a.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-medium text-slate-100 truncate">{a.title}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10.5px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-white/5 text-slate-400">{labelize(a.category)}</span>
                      <span className="text-[11px] text-slate-500">Updated {fmtDate(a.updatedAt)}</span>
                    </div>
                  </div>
                  <span
                    data-testid={`help-article-status-${a.id}`}
                    className={`shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${a.status === "published" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}
                  >
                    {a.status === "published" ? "Published" : "Draft"}
                  </span>
                  <button onClick={() => setEditing(a)} data-testid={`help-article-edit-${a.id}`} className="p-2 rounded-md text-slate-400 hover:bg-white/5 hover:text-slate-200">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => remove(a)} disabled={busyId === a.id} data-testid={`help-article-delete-${a.id}`} className="p-2 rounded-md text-slate-400 hover:bg-white/5 hover:text-rose-300">
                    {busyId === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ---- Chatbot settings ---- */}
      <div className="rounded-xl border border-white/10 bg-[#0F1623] p-4" data-testid="help-center-chatbot-settings">
        <div className="flex items-center gap-2 mb-3">
          {settings?.enabled ? <MessageSquare className="h-4 w-4 text-emerald-400" /> : <MessageSquareOff className="h-4 w-4 text-slate-500" />}
          <h3 className="text-[14px] font-semibold text-slate-100">Chatbot Settings</h3>
        </div>
        {!settings ? (
          <div className="py-8 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-500" /></div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-[11.5px] font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Fallback message</label>
              <textarea
                value={settings.fallbackMessage}
                onChange={(e) => setSettings({ ...settings, fallbackMessage: e.target.value })}
                rows={3}
                data-testid="help-center-fallback-input"
                className="w-full px-3 py-2 rounded-lg bg-[#0B0F17] border border-white/10 text-[13px] text-slate-200 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
              />
              <p className="text-[11.5px] text-slate-500 mt-1">Shown when the chatbot can't find a confident answer in the knowledge base.</p>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-white/10 bg-[#0B0F17] px-3.5 py-3">
              <div>
                <div className="text-[13px] font-medium text-slate-200">Chatbot enabled</div>
                <div className="text-[11.5px] text-slate-500 mt-0.5">Turn off to disable the Help &amp; Support chatbot entirely — every question will show the fallback message.</div>
              </div>
              <button
                type="button"
                onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
                data-testid="help-center-enabled-toggle"
                className={`relative h-6 w-11 rounded-full shrink-0 ml-3 transition-colors ${settings.enabled ? "bg-emerald-500" : "bg-slate-600"}`}
              >
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${settings.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </div>
            <div className="flex justify-end">
              <button
                onClick={saveSettings}
                disabled={savingSettings}
                data-testid="help-center-save-settings-btn"
                className="h-9 px-4 rounded-md bg-cyan-600 hover:bg-cyan-500 text-white text-[13px] font-semibold disabled:opacity-50"
              >
                {savingSettings ? "Saving..." : "Save Settings"}
              </button>
            </div>
          </div>
        )}
      </div>

      {editing !== null && (
        <HelpArticleEditorModal
          article={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            loadArticles();
          }}
        />
      )}
    </div>
  );
}
